import { inject, Injectable } from '@angular/core';
import type { FrontendTool, HostContext } from '@visma-swno/gaia-chat-ui';
import { ENTITY_ITEM_ID_KEYS_MAP } from '../core/const/entity-item-id-keys-map';
import { ENTITY_TYPE } from '../core/const/entity-type';
import { CONTEXT_TYPE } from '../core/auth/context-type';
import { ProfileService } from '../core/auth/profile.service';
import { I18NService } from '../core/i18n/i18n.service';
import { AlertFlashDataService } from '../alert/alert-flash/alert-flash-data.service';
import { AlertFlashMessage } from '../alert/alert-flash/alert-flash-message';
import { ALERT_FLASH_NAME } from '../alert/alert-flash/alert-flash-name';
import { ServiceAccessDataService } from '../service-access/service-access-data.service';
import { DISABLE_ROWS_LIMIT, ROWS_LIMIT } from '../service-access/service-access-limit';
import { SelectableListRegistry } from './selectable-list-registry.service';
import { SelectableList, SelectionOutcome } from './selectable-list';

/**
 * The tools the GAiA assistant may call in this app, and the context every message carries.
 *
 * The boundary is the point of the whole feature: **the assistant never grants.** Nothing here
 * reaches `ServiceAccessService.save()` or any other write — the only state these handlers
 * touch is what is selected on screen, in the administrator's own browser, under their own
 * session. They can do nothing the administrator could not do by clicking. Granting stays a
 * human press on the Grant button.
 *
 * Note this is registered on the chat element on every screen, not only on user access. A tool
 * that silently disappeared would leave the model unable to explain itself; instead the
 * handlers answer that the screen is not open, which it can pass on as "open User access
 * first".
 */
@Injectable({ providedIn: 'root' })
export class AssistantToolsService {

    private readonly serviceAccessDataService = inject(ServiceAccessDataService);
    private readonly registry = inject(SelectableListRegistry);
    private readonly profileService = inject(ProfileService);
    private readonly alertFlashDataService = inject(AlertFlashDataService);
    private readonly i18nService = inject(I18NService);

    /**
     * Longer than the 2s default: this one explains why the screen did not change, and the
     * administrator is reading the chat when it appears, not the grid.
     */
    private static readonly REFUSAL_ALERT_MS = 6000;

    /**
     * The entity types each column can be on, as the agent names them, against the two things
     * the app names differently.
     *
     * Both halves are easy to get wrong and neither fails loudly. `ENTITY_TYPE` values are
     * PascalCase (`'User'`, and `customer` is `'CustomerOfVisma'`), which is what
     * `getSubjectTypeSelected()` and friends return — while `ENTITY_ITEM_ID_KEYS_MAP` is keyed
     * by the *property* names instead, so `ENTITY_ITEM_ID_KEYS_MAP[ENTITY_TYPE.user]` is
     * `undefined`, not `'connectUserId'`. One table, spelled out, rather than two lookups that
     * look interchangeable and are not.
     */
    private static readonly TYPES: Readonly<Record<string, { entityType: string; idKey: string }>> = {
        user: { entityType: ENTITY_TYPE.user, idKey: ENTITY_ITEM_ID_KEYS_MAP['user'] },
        supportUser: { entityType: ENTITY_TYPE.supportUser, idKey: ENTITY_ITEM_ID_KEYS_MAP['supportUser'] },
        userGroup: { entityType: ENTITY_TYPE.userGroup, idKey: ENTITY_ITEM_ID_KEYS_MAP['userGroup'] },
        company: { entityType: ENTITY_TYPE.company, idKey: ENTITY_ITEM_ID_KEYS_MAP['company'] },
        clientCompany: { entityType: ENTITY_TYPE.clientCompany, idKey: ENTITY_ITEM_ID_KEYS_MAP['clientCompany'] },
        companyGroup: { entityType: ENTITY_TYPE.companyGroup, idKey: ENTITY_ITEM_ID_KEYS_MAP['companyGroup'] },
        customer: { entityType: ENTITY_TYPE.customer, idKey: ENTITY_ITEM_ID_KEYS_MAP['customerOfVisma'] },
        role: { entityType: ENTITY_TYPE.role, idKey: ENTITY_ITEM_ID_KEYS_MAP['role'] },
        // Role groups and system role groups are deliberately absent. Their lists override
        // `onInitTable()` without calling super, so they never register and could never be
        // selected — and advertising a type in the schema that cannot work is the one failure an
        // agent cannot recover from: it would be told the column is not on screen while looking
        // at it. Out of scope for v1 anyway (plan §6). Add them here when they register.
    };

    /**
     * Whether the administrator has said something since the last selection was made.
     *
     * One selection per message of theirs, and the reason is what happens *after* the agent has
     * described what it prepared. Granting applies whatever is selected at the moment the button
     * is pressed, not what the agent said a minute earlier — so without this, a second
     * `select_access_for_grant` could replace a selection the administrator had already read and
     * agreed to, and they would confirm something other than what they were shown. The whole
     * mitigation for tenant-data prompt injection is that a human reviewed the list; a review
     * that can be invalidated afterwards is not one.
     *
     * Keyed on the administrator speaking rather than on anything the agent reports, because an
     * agent that has been talked into a second selection is exactly the agent whose word cannot
     * be used to decide whether it may have one. `<gaia-chat>` fires `message-sent` only for a
     * typed message or a clicked suggested question, and never for the continuation run that
     * follows a tool call — so the agent has no way to renew this for itself.
     *
     * Starts false: the agent only ever runs in response to a message, so the allowance for a
     * legitimate call is always granted before the call can happen. `nav-shell` wires the event
     * in the same block that registers the tools, so there is no arrangement where the tools
     * exist and this never opens.
     */
    private selectionAllowed = false;

    /**
     * The administrator sent a message, which re-opens the one selection it is worth.
     *
     * Public because the host component owns the element's events; called from `nav-shell`'s
     * `message-sent` listener.
     */
    allowSelection(): void {
        this.selectionAllowed = true;
    }

    private static readonly SUBJECT_TYPES = ['user', 'supportUser', 'userGroup'];
    private static readonly CONTEXT_TYPES = ['company', 'clientCompany', 'companyGroup', 'customer'];
    private static readonly ASSIGNMENT_TYPES = ['role'];
    private static readonly COLUMNS = ['subject', 'context', 'assignment'];

    /**
     * The id keys the three user-access columns select on — this tool's whole surface.
     *
     * Read from TYPES rather than listed again, and used instead of the registry's full
     * contents: `SelectableListRegistry` is generic and other screens register into it too (the
     * on-premises integration lists mount as `main` just like these do). Asking it "is anything
     * mounted?" answers a different question than "is the user-access screen open?".
     */
    private static idKeys(): string[] {
        return [...new Set(Object.values(AssistantToolsService.TYPES).map(t => t.idKey))];
    }

    /** The agent-facing name for an `ENTITY_TYPE` value, so context and tool speak one vocabulary. */
    private static nameOf(entityType: string): string {
        return Object.keys(AssistantToolsService.TYPES)
            .find(name => AssistantToolsService.TYPES[name].entityType === entityType) ?? entityType;
    }

    /** The user-access columns that are on screen right now, by id key. */
    private mountedColumns(): string[] {
        return AssistantToolsService.idKeys().filter(idKey => this.registry.get(idKey) !== undefined);
    }

    getTools(): FrontendTool[] {
        return [
            {
                name: 'get_admin_ui_selection',
                description:
                    'Read what is currently selected on the Admin user-access screen: the selected users, '
                    + 'companies and roles, and how many service-access rows each of the two tables holds. '
                    + 'Read-only — it changes nothing.',
                parameters: { type: 'object', properties: {} },
                handler: () => this.readSelection(),
            },
            {
                name: 'find_in_admin_ui_list',
                description:
                    'Search one column of the Admin user-access screen by name and return the matching rows '
                    + 'with their ids, which select_access_for_grant accepts. Use it to resolve a user, company '
                    + 'or role the administrator named when the admin plugin cannot — a role nobody holds yet, '
                    + 'or a user or company outside what you have compared. It searches the type the column is '
                    + 'showing now. Read-only — it changes nothing on screen.',
                parameters: {
                    type: 'object',
                    properties: {
                        column: { enum: AssistantToolsService.COLUMNS },
                        query: { type: 'string', minLength: 1 },
                    },
                    required: ['column', 'query'],
                },
                handler: (args) => this.findInList(args),
            },
            {
                name: 'select_access_for_grant',
                description:
                    'Prepare the Admin user-access screen so the administrator can grant access: selects one '
                    + 'subject, the given companies and the given roles, which makes the missing assignments '
                    + "appear in the screen's 'Possible service access' table. This ONLY changes what is "
                    + 'selected on screen — it does not grant anything, and cannot. The administrator must '
                    + 'press Grant themselves afterwards. '
                    // Said up front as well as on refusal: an agent that knows the shape of the
                    // limit can ask for a set that fits, instead of learning it by being turned
                    // away once per attempt.
                    + `The screen cannot show more than ${ROWS_LIMIT} role assignments at a time, and what `
                    + 'counts is users × companies × roles multiplied together, not each on its own — so '
                    + 'ask for a set whose product stays within it, and do a wide alignment as several '
                    + 'calls, each one granted before the next.',
                parameters: {
                    type: 'object',
                    properties: {
                        subjectType: { enum: AssistantToolsService.SUBJECT_TYPES, default: 'user' },
                        subjectIds: { type: 'array', items: { type: 'string' }, maxItems: 1 },
                        contextType: { enum: AssistantToolsService.CONTEXT_TYPES, default: 'company' },
                        contextIds: { type: 'array', items: { type: 'string' } },
                        assignmentType: { enum: AssistantToolsService.ASSIGNMENT_TYPES, default: 'role' },
                        assignmentIds: { type: 'array', items: { type: 'string' } },
                    },
                    required: ['subjectIds', 'contextIds', 'assignmentIds'],
                },
                handler: (args) => this.selectAccessForGrant(args),
            },
        ];
    }

    /**
     * Sent with every message, so the assistant knows which screen and which customer it is
     * talking about without having to ask or call a tool first.
     */
    getHostContext(): HostContext {
        const screenOpen = this.mountedColumns().length > 0;
        return {
            state: {
                application: 'admin-user-access',
                userAccessScreenOpen: screenOpen,
                // Only when there is a screen for them to describe. `ServiceAccessDataService` is
                // root-scoped and keeps its column types across a navigation, so off the user-access
                // screen these are either its constructor defaults or whatever the administrator was
                // last looking at - and on the product screen, which no column can ever be on, they
                // are always the former. The agent was being handed the shape of a screen that is
                // not there, next to a flag saying it is not there. `selection` stays either way:
                // an empty object is an accurate answer, and its emptiness follows from the same flag.
                ...(screenOpen ? this.columnTypes() : {}),
                selection: this.selectionByColumn(),
            },
            props: this.contextProps(),
        };
    }

    /**
     * The ids forwarded to the backend plugins — `props` is documented as tenant identification,
     * and its vocabulary (`customerId`, `companyId`, `partnerId`) is the library's, not ours.
     *
     * So `customerId` is only sent where the current context IS a customer. It used to be
     * `currentContext.contextId` unconditionally, which is right for the screen this was written
     * for and wrong everywhere else: on a Distributor that field holds the business unit's id, and
     * it was being handed to the agent under a name that says customer. Measured on stag, on the
     * Distributor `Visma Software A/S`. A Distributor has no single customer to name in any case —
     * its user-access screen puts *customers* in the context column, several at a time.
     *
     * Absent rather than renamed: `partnerId` is the obvious home for a business unit and
     * `companyId` for a Company context, but which ids the `admin` plugin actually reads is on the
     * other side of the vsn-assistant repo. Sending nothing cannot mislead it; guessing a key can.
     * Fill these in when that side is confirmed.
     *
     * Note `contextType` means something else three lines up: there it is the entity type the
     * context *column* is showing (`'company'`), here it is how Connect classifies the signed-in
     * context (`'Distributor'`). Same word, two contracts, and the agent sees both.
     */
    private contextProps(): Record<string, unknown> {
        const context = this.profileService.profile.currentContext;
        const isCustomerContext = context?.contextType === CONTEXT_TYPE.customerOfVisma
            || context?.contextType === CONTEXT_TYPE.customer;
        return isCustomerContext ? { customerId: context.contextId } : {};
    }

    private columnTypes(): { subjectType: string; contextType: string; assignmentType: string } {
        return {
            subjectType: AssistantToolsService.nameOf(this.serviceAccessDataService.getSubjectTypeSelected()),
            contextType: AssistantToolsService.nameOf(this.serviceAccessDataService.getContextTypeSelected()),
            assignmentType: AssistantToolsService.nameOf(this.serviceAccessDataService.getAssignmentTypeSelected()),
        };
    }

    private readSelection(): unknown {
        if (this.mountedColumns().length === 0) {
            return this.screenNotOpen();
        }
        return {
            ...this.columnTypes(),
            selection: this.selectionByColumn(),
            currentServiceAccessRows: this.serviceAccessDataService.getCurrentAccessRowCount(),
            possibleServiceAccessRows: this.serviceAccessDataService.getPossibleAccessRowCount(),
        };
    }

    /**
     * Resolves a name to ids in one column, in the type that column is on — the one
     * `select_access_for_grant` will check against, so an id found here is never refused as
     * `wrong_column_type` afterwards.
     *
     * Deliberately outside the one-selection-per-message allowance: it reads, and an agent that
     * could not look up a role before selecting it would have nothing to select.
     */
    private async findInList(args: Record<string, unknown>): Promise<unknown> {
        if (this.mountedColumns().length === 0) {
            return this.screenNotOpen();
        }

        const column = this.asString(args['column'], '');
        if (!AssistantToolsService.COLUMNS.includes(column)) {
            return {
                error: 'unknown_column',
                message: `"${column}" is not a column. Use subject, context or assignment.`,
            };
        }

        const query = this.asString(args['query'], '').trim();
        if (query.length === 0) {
            return { error: 'empty_query', message: 'Give part of the name to search for.' };
        }

        const types = this.columnTypes();
        const type = { subject: types.subjectType, context: types.contextType, assignment: types.assignmentType }[column] ?? '';
        const idKey = AssistantToolsService.TYPES[type]?.idKey;
        const list = idKey ? this.registry.get(idKey) : undefined;
        if (!list) {
            return {
                error: 'column_not_available',
                message: `The ${column} column is showing "${type}", which cannot be searched or selected. `
                    + 'Ask the administrator to switch that column first.',
            };
        }

        return { column, type, query, ...(await list.search(query)) };
    }

    private selectionByColumn(): Record<string, { id: string; name: string }[]> {
        const selection: Record<string, { id: string; name: string }[]> = {};
        for (const idKey of this.mountedColumns()) {
            selection[idKey] = this.registry.get(idKey)?.getSelection() ?? [];
        }
        return selection;
    }

    private async selectAccessForGrant(args: Record<string, unknown>): Promise<unknown> {
        if (this.mountedColumns().length === 0) {
            return this.refuse(this.screenNotOpen());
        }

        // Checked here and spent further down, on purpose. A call turned away for a wrong column
        // type or a set that is too wide is one the agent can fix and retry within the same
        // message — the refusals say so in as many words — so a failed attempt must not cost the
        // allowance. Only a selection that actually reached the lists does.
        if (!this.selectionAllowed) {
            return this.refuse({
                error: 'selection_already_made',
                message: 'A selection has already been made for what the administrator last asked, and it is '
                    + 'on screen now. Tell them what is waiting and let them press Grant. If something else '
                    + 'is needed, ask — their next message allows another selection. Do not select again on '
                    + 'your own: it would replace what they have already been shown, and Grant applies what '
                    + 'is selected when they press it.',
            });
        }

        const current = this.columnTypes();
        const columns = [
            { column: 'subject', type: this.asString(args['subjectType'], 'user'), ids: this.asIds(args['subjectIds']), current: current.subjectType },
            { column: 'context', type: this.asString(args['contextType'], 'company'), ids: this.asIds(args['contextIds']), current: current.contextType },
            { column: 'assignment', type: this.asString(args['assignmentType'], 'role'), ids: this.asIds(args['assignmentIds']), current: current.assignmentType },
        ];

        const unknownType = columns.find(c => !AssistantToolsService.TYPES[c.type]);
        if (unknownType) {
            return this.refuse({
                error: 'unknown_entity_type',
                message: `"${unknownType.type}" is not an entity type the ${unknownType.column} column can show.`,
            });
        }

        // Switching a column's type is its own UI action, driven by a different stream, and is
        // not implemented here. Refusing is the honest answer: acting on the wrong column would
        // select real ids into the wrong list rather than fail.
        const wrongType = columns.find(c => c.type !== c.current);
        if (wrongType) {
            return this.refuse({
                error: 'wrong_column_type',
                message: `The ${wrongType.column} column is showing "${wrongType.current}", not "${wrongType.type}". `
                    + 'Ask the administrator to switch that column first.',
            });
        }

        const empty = columns.find(c => c.ids.length === 0);
        if (empty) {
            return this.refuse({
                error: 'nothing_to_select',
                message: `No ids were given for the ${empty.column} column. All three are needed before the `
                    + 'screen can offer anything to grant.',
            });
        }

        const tooWide = this.overRowLimit(columns);
        if (tooWide) {
            return this.refuse(tooWide);
        }

        // Resolve every column before touching any of them. Refusing halfway used to leave the
        // first two cleared and re-selected while reporting failure.
        const targets: { idKey: string; list: SelectableList; ids: string[] }[] = [];
        for (const { column, type, ids } of columns) {
            const idKey = AssistantToolsService.TYPES[type].idKey;
            const list = this.registry.get(idKey);
            if (!list) {
                return this.refuse({
                    error: 'column_not_available',
                    message: `The ${column} column is not on screen.`,
                });
            }
            targets.push({ idKey, list, ids });
        }

        // Every clear before any selection, and not merely for tidiness: `clearSelections()`
        // also calls `resetConnectUserIdsBelongsToACustomerArray()`. Clearing a column after the
        // subject was selected therefore discards the `belongsTo` id that selection had just
        // recorded — and `onClickGrant` reads that array to decide whether the user still needs
        // linking to the customer, so the link would silently be skipped on Grant.
        // Spent at the last moment before the screen changes: everything above this line can be
        // corrected and retried inside the same message, and nothing below it leaves the screen
        // as it was.
        this.selectionAllowed = false;

        targets.forEach(target => target.list.clearSelectedItems());

        const unresolved: Record<string, SelectionOutcome> = {};
        for (const { idKey, list, ids } of targets) {
            const outcome = await list.selectByIds(ids);
            if (outcome.notFound.length > 0 || outcome.refused.length > 0) {
                unresolved[idKey] = outcome;
            }
        }

        return {
            selected: this.selectionByColumn(),
            // `refused` is separated from `notFound` on purpose: it means the id was real but the
            // column is already at its 20-item limit, which the agent can act on by asking for
            // fewer rather than by correcting an id.
            ...(Object.keys(unresolved).length > 0 ? { unresolved } : {}),
            granted: false,
            nextStep: 'Nothing has been granted. The administrator must review the "Possible service access" '
                + 'table and press Grant, or Grant all.',
        };
    }

    /**
     * Refuses a request whose cross product the screen cannot show at all — plan §4.3.
     *
     * Not a truncation. When the response's `totalNumberOfRecords` passes `ROWS_LIMIT`,
     * `base-service-access.component.ts` opens the limit-warning dialog and never reaches
     * `filterResponse()`, so **neither** table is filled: the administrator gets a modal over
     * two empty grids, not a partial alignment. There is no smaller thing to usefully
     * half-do, and selecting first and discovering it after would leave the screen in exactly
     * the state this refusal exists to avoid.
     *
     * Counted against what the columns would actually hold rather than against what was asked
     * for: each list stops at `DISABLE_ROWS_LIMIT`, so 25 companies and 5 roles is 100 rows,
     * not 125, and refusing that would refuse a selection that fits. The subject column is in
     * the product too — it is capped at one by the tool's schema, but a schema is a request,
     * not a guarantee, and three subjects multiply the matrix exactly like anything else.
     *
     * The administrator is told as well as the agent. The refusal is otherwise invisible to
     * the person watching the screen: they asked for something, nothing moved, and only the
     * chat would know why. Reuses the sentence the limit dialog shows for the same condition
     * — same words for the same rule, already translated in all seven locales.
     */
    private overRowLimit(columns: { column: string; type: string; ids: string[] }[]): { error: string; message: string } | undefined {
        // Named by entity type, not by column role: "20 company × 10 role" is the sentence the
        // administrator would say, and the three type names are the three dials the agent can
        // actually turn. "20 context × 10 assignment" is this app's own vocabulary and means
        // nothing on the other side.
        const held = columns.map(c => ({ type: c.type, count: Math.min(c.ids.length, DISABLE_ROWS_LIMIT) }));
        const rows = held.reduce((product, c) => product * c.count, 1);
        if (rows <= ROWS_LIMIT) {
            return undefined;
        }

        const text = this.i18nService.getFormattedMessage(
            this.i18nService.messages['common']?.['infoItIsCurrentlyNotPossibleToMakeSelectionsResultingInMoreThanXRoleAssignments']
            ?? 'It is currently not possible to make selections resulting in more than {0} role assignments',
            [ROWS_LIMIT]);
        this.alertFlashDataService.addAlertMessage(
            ALERT_FLASH_NAME.main,
            AlertFlashMessage.TYPE.warning,
            text,
            AssistantToolsService.REFUSAL_ALERT_MS);

        // Hoisted out of the message rather than inlined: a template literal nested inside
        // another is hard to read and Sonar flags it (S4624). It is also the one phrase here
        // worth naming - it is what the administrator would say out loud.
        const shape = held.map(c => `${c.count} ${c.type}`).join(' × ');

        return {
            error: 'over_row_limit',
            message: `${shape} is ${rows} rows, and the screen `
                + `cannot show more than ${ROWS_LIMIT} — past that it opens a warning dialog and fills neither `
                + 'table. Nothing was selected and nothing was cleared, and the administrator has been shown a '
                + 'warning. Call again with fewer of any of the three — fewer companies, fewer roles or fewer '
                + 'users — so the three multiplied stay within the limit, and do the rest in a later call once '
                + 'this one has been granted.',
        };
    }

    /**
     * Adds `granted: false` to a refusal, so every response from `select_access_for_grant`
     * carries it and not only the one that reports a selection.
     *
     * The field is not a guard: nothing in this app reads it, and what makes a grant
     * impossible is that no path from this handler reaches `ServiceAccessService.save()` —
     * it clears and selects, and that is all it can do. It is addressed to the agent, whose
     * failure mode is telling the administrator that access was granted. A marker present on
     * some responses and absent on others is one the agent cannot rely on, and a refusal is
     * the moment it matters most: nothing moved on screen, so the agent has to explain
     * itself with no selection to describe.
     *
     * `get_admin_ui_selection` deliberately does not carry it. `granted` is this tool's
     * vocabulary, and the read tool has no such field on its success response either — which
     * is why `screenNotOpen()`, shared by both, stays plain and is wrapped here instead.
     */
    private refuse(refusal: { error: string; message: string }): { error: string; message: string; granted: false } {
        return { ...refusal, granted: false };
    }

    private screenNotOpen(): { error: string; message: string } {
        return {
            error: 'user_access_screen_not_open',
            message: 'The Admin user-access screen is not open, so there is nothing to read or select. '
                + 'Ask the administrator to open User access first.',
        };
    }

    private asString(value: unknown, fallback: string): string {
        return typeof value === 'string' && value.length > 0 ? value : fallback;
    }

    /**
     * Normalises one column's ids as they arrive from the agent: strings only, each once.
     *
     * The de-duplication is about how the list is built, not about the data — an entity has one
     * id. Deriving the companies from a per-assignment difference is naturally
     * `diff.map(r => r.tenantId)`, and a difference of nine roles in one company yields that
     * company nine times. Normalising here is this method's job; the lists it feeds have no
     * business knowing the caller repeated itself.
     */
    private asIds(value: unknown): string[] {
        if (!Array.isArray(value)) {
            return [];
        }
        const ids = value.filter((id): id is string => typeof id === 'string' && id.length > 0);
        return [...new Set(ids)];
    }
}
